import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Globe, Phone, Trash2, Pencil, Tag } from "lucide-react";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Input, Spinner, Empty, IconBtn } from "@/components/ui-kit";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";

const SupplierRow = ({ s, onDelete }) => (
  <Card data-testid={`supplier-card-${s.id}`} className="px-5 py-4 hover:border-zinc-300 hover:shadow-sm transition-all">
    <div className="flex items-center gap-6">
      <div className="flex-1 min-w-0">
        <Link to={`/suppliers/${s.id}/edit`} className="block">
          <h3 className="font-display text-lg font-semibold text-zinc-950 hover:text-zinc-700 transition-colors truncate">{s.name}</h3>
        </Link>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-500">
          {s.type && (
            <span className="inline-flex items-center gap-1.5">
              <Tag className="w-3 h-3 text-zinc-400" />
              <span className="text-zinc-700">{s.type}</span>
            </span>
          )}
          {s.website && (
            <a
              href={/^https?:\/\//.test(s.website) ? s.website : `https://${s.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-zinc-900 truncate max-w-[260px]"
              onClick={(e) => e.stopPropagation()}
            >
              <Globe className="w-3 h-3 text-zinc-400" />
              <span className="truncate">{s.website}</span>
            </a>
          )}
          {s.phone && (
            <span className="inline-flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-zinc-400" />
              {s.phone}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to={`/suppliers/${s.id}/edit`} data-testid={`supplier-edit-${s.id}`}>
          <Btn variant="secondary" size="sm"><Pencil className="w-3.5 h-3.5" />Edit</Btn>
        </Link>
        <IconBtn variant="destructive" onClick={() => onDelete(s.id)} data-testid={`supplier-delete-${s.id}`}>
          <Trash2 className="w-4 h-4" />
        </IconBtn>
      </div>
    </div>
  </Card>
);

const SuppliersList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/suppliers");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!(await confirm({ title: "Delete this supplier?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/suppliers/${id}`);
    await load();
  };

  const filtered = items.filter((s) =>
    [s.name, s.type, s.website, s.phone].filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Shell>
      <PageHeader
        testId="suppliers-header"
        title="Suppliers"
        subtitle="Your printers, finishers and trade partners"
        actions={
          <Btn onClick={() => navigate("/suppliers/new")} data-testid="new-supplier-btn">
            <Plus className="w-4 h-4" />New Supplier
          </Btn>
        }
      />

      <div className="mb-6 max-w-sm">
        <Input
          data-testid="suppliers-search"
          placeholder="Search by name, type, website, phone..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <Empty
          title="No suppliers yet"
          description="Add your first supplier to start tracking internal costs on quotes."
          action={<Btn onClick={() => navigate("/suppliers/new")}><Plus className="w-4 h-4" />New Supplier</Btn>}
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          {filtered.map((s) => <SupplierRow key={s.id} s={s} onDelete={del} />)}
        </div>
      )}
    </Shell>
  );
};

export default SuppliersList;
